import type { Element, RootContent } from 'hast';
import React, { Fragment, PropsWithChildren, useMemo } from 'react';
import { Components } from 'react-markdown';

import SourceLink from '@/components/atom/SourceLink/SourceLink';
import TableGraphSwitcher from '@/components/molecule/TableGraphSwitcher/TableGraphSwitcher';
import TableActionBar from '@/components/organism/TableActionBar/TableActionBar';
import { resolveSource } from '@/utils/resolveSource/resolveSource';
import { extractLinksFromListSubtree } from '@/utils/urlHandler/urlHandler';

import CardDropdown from '../CardDropdown/CardDropdown';
import LimitedList from '../LimitedList/LimitedList';
import Table from '../Table/Table';
import { StyledTable, StyledTd, StyledTh, StyledThead, StyledTr, TableContainer } from './Style';
import type { TMarkdownRenderersProps } from './types';
import { shouldShowCard, toLinkText } from './utils';

const useExtractedLinks = (node: RootContent | undefined) => useMemo(() => extractLinksFromListSubtree(node), [node]);

// TODO: Reduce prop drilling here since data visualization can be obtained through the messageIdmessageId
const MarkdownRenderers = ({
  message,
  messageId,
  showActions,
  data_visualization,
  opensSourcesInViewer = false,
}: TMarkdownRenderersProps): Components => {
  return {
    pre: ({ children }: PropsWithChildren) => <Fragment>{children}</Fragment>,
    code: ({ className, children }: PropsWithChildren<{ className?: string }>) => {
      const isJson = className?.includes('language-json');

      if (isJson) {
        try {
          if (!children) {
            throw new Error('No JSON content found');
          }
          const jsonString = Array.isArray(children) ? children.join('') : children.toString();
          const jsonData = JSON.parse(jsonString);

          if (jsonData.table && Array.isArray(jsonData.table)) {
            const card = <CardDropdown>{jsonData.table}</CardDropdown>;

            const tableGraphSwitcher = (
              <TableGraphSwitcher
                data={jsonData.table}
                showActions={showActions}
                data_visualization={data_visualization ?? null}
                messageId={messageId}
                message={message}
              />
            );

            return shouldShowCard(children) ? card : tableGraphSwitcher;
          }
          return (
            <>
              <TableContainer className="table-responsive">
                <Table data={jsonData} />
              </TableContainer>
              {showActions ? <TableActionBar message={message} /> : null}
            </>
          );
        } catch (error) {
          console.error('Failed to parse JSON:', error);
          return <pre className={className}>{children}</pre>;
        }
      }
      return <pre className={className}>{children}</pre>;
    },
    // @ts-expect-error — react-markdown renderer props type doesn't match custom PropsWithChildren<{ href: string }>
    a: ({ href, children, node }: PropsWithChildren<{ href: string; node?: Element }>) => (
      <SourceLink
        href={href}
        messageId={messageId}
        offset={node?.position?.start.offset}
        source={opensSourcesInViewer ? resolveSource(href, toLinkText(children)) : null}
      >
        {children}
      </SourceLink>
    ),
    p: ({ children }: PropsWithChildren) => <div className="padding-y-5">{children}</div>,

    // #region Lists
    // INFO: Since there's a need to only collapse lists when it is a list of documents, we need to inspect the provided children for the given list

    ul: ({ children, node }) => {
      const extractedLinks = useExtractedLinks(node);

      return (
        <LimitedList urlsContained={extractedLinks.total} elementType="ul">
          {children}
        </LimitedList>
      );
    },

    ol: ({ children, start, node }) => {
      const extractedLinks = useExtractedLinks(node);

      return (
        <LimitedList urlsContained={extractedLinks.total} elementType="ol" start={start}>
          {children}
        </LimitedList>
      );
    },

    // #region Lists

    li: ({ children }: PropsWithChildren) => <li className="margin-left-20-ls padding-0">{children}</li>,

    table: ({ children }: PropsWithChildren) => {
      return (
        <div className="width-100pct">
          <TableContainer className="table-responsive">
            <StyledTable className="table table-head-filled width-100pct">{children}</StyledTable>
          </TableContainer>
          <TableActionBar message={message} />
        </div>
      );
    },
    th: ({ children }: PropsWithChildren) => {
      return <StyledTh>{children}</StyledTh>;
    },
    td: ({ children }: PropsWithChildren) => {
      return <StyledTd>{children}</StyledTd>;
    },
    tr: ({ children }: PropsWithChildren) => {
      return (
        // @ts-expect-error — React.Children types don't narrow correctly for cloneElement
        <StyledTr>{React.Children.map(children, (child, index) => React.cloneElement(child, { index }))}</StyledTr>
      );
    },
    tbody: ({ children }: PropsWithChildren) => {
      return <tbody>{children}</tbody>;
    },
    thead: ({ children }: PropsWithChildren) => {
      return <StyledThead show={!shouldShowCard(children)}>{children}</StyledThead>;
    },
  };
};

export default MarkdownRenderers;
